<template>
  <!-- h-100: fills the dashboard row, so it matches Ride Requests' height beside it. -->
  <dashboard-tile class="h-100 p-3 d-flex flex-column" :loading="isLoading" loader-height="491px">
    <div class="d-flex flex-wrap w-100 justify-content-between align-items-center">
      <h6 class="dashboard-tile-title">{{ $t('flexigo.dashboard.numberOfRidersByDayOfTheWeek') }}</h6>
      <div class="align-self-end d-flex align-items-center gap-2">
        <route-multiselect
          :routes="value.routes"
          :selected-route-ids="selectedRouteIds"
          :no-routes="noRoutes"
          @change="selectRoutes"
        />
        <!-- The same menu and items as Occupancy Ratios and the Ridership charts. -->
        <export-menu
          :formats="['png', 'pdf', 'print', 'excel']"
          :loading-format="isDownloading ? 'excel' : ''"
          @export="handleExport"
        />
      </div>
    </div>
    <div ref="chartArea" class="chart-area mt-3">
      <highcharts ref="chart" class="h-100" :options="chartOptions" />
    </div>
  </dashboard-tile>
</template>
<script>
import Highcharts from 'highcharts';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsOfflineExporting from 'highcharts/modules/offline-exporting';
import { Chart } from 'highcharts-vue';
import { weekdaysUs } from '@/helpers/constants';
import { cssTokens } from '@/modules/Flexigo/helpers/cssToken';
import downloadRidershipReport from '@/modules/Flexigo/helpers/ridershipReport';
import DashboardTile from './DashboardTile.vue';
import RouteMultiselect from './RouteMultiselect.vue';
import ExportMenu from './otp/components/ExportMenu.vue';
import { exportChartAsPNG, exportChartAsPDF, printChart } from './otp/utils/chartExport';
import toaster from '../../mixins/toaster';

// The PDF export needs the offline-exporting module, so this chart registers it rather than rely on another chart on
// the page having done so. Highcharts is a singleton and registering is idempotent.
HighchartsExporting(Highcharts);
HighchartsOfflineExporting(Highcharts);

// One column series per working type, its tooltip naming the routes the riders embarked on.
const ridersSeries = (name, color, data, routesText) => ({
  type: 'column',
  name,
  color,
  data,
  tooltip: {
    headerFormat: '{point.x}<br/><br/>',
    pointFormatter() {
      return `<div style=" font-size: 0.8rem; "><strong>${this.y}</strong> Riders</div><br/> ${routesText}`;
    },
  },
});

export default {
  name: 'RidersGraph',
  components: { highcharts: Chart, DashboardTile, RouteMultiselect, ExportMenu },
  mixins: [toaster],
  props: {
    value: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    // Routes the data on screen was fetched for; empty means every route. Owned by the dashboard,
    // which does the fetching.
    selectedRouteIds: {
      type: Array,
      default: () => [],
    },
    // The header's route groups have no routes: the filter says so, and the data is empty.
    noRoutes: {
      type: Boolean,
      default: false,
    },
    // The Excel export's route scope (see the dashboard's exportRouteScope): [] for every route, null when it's
    // blocked because the picked route groups' routes are unknown or there are none.
    exportRouteIds: {
      type: Array,
      default: () => [],
    },
    // The header's range as YYYYMMDD days, { startDay, endDay }, for the Excel export.
    reportDays: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isDownloading: false,
    };
  },
  computed: {
    // A missing working type (no fixed routes, say) plots as zeros.
    chartOptions() {
      const perWeekday = (stats) => weekdaysUs.map((day) => stats?.[day] || 0);
      return {
        chart: { type: 'column' },
        credits: { enabled: false },
        // The header's export menu drives the exports; the chart's own button is off.
        exporting: { enabled: false },
        title: { text: null },
        xAxis: {
          categories: ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].map((day) => this.$t(`flexigo.common.${day}`)),
          gridLineWidth: 1,
        },
        yAxis: { title: { text: null } },
        tooltip: {
          shared: false,
          backgroundColor: this.colors['--color-text'],
          style: { color: this.colors['--color-text-inverse'] },
          borderRadius: 4,
          borderWidth: 0,
        },
        legend: {
          symbolType: 'square',
          itemStyle: { color: this.colors['--color-text-secondary'], fontSize: '10px' },
        },
        series: [
          ridersSeries(
            this.$t('flexigo.dashboard.ridersEmbarkedOnFixedRoutes'),
            this.colors['--color-primary'],
            perWeekday(this.value.FIXED),
            'embarked on fixed routes',
          ),
          ridersSeries(
            this.$t('flexigo.dashboard.ridersEmbarkedOnDynamicRoutes'),
            this.colors['--color-progress'],
            perWeekday(this.value.DYNAMIC),
            'embarked on dynamic routes',
          ),
        ],
      };
    },
  },
  watch: {
    // The chart area exists only while not loading.
    isLoading: {
      immediate: true,
      handler(loading) {
        if (loading) this.stopFollowingSize();
        else this.$nextTick(this.followSize);
      },
    },
  },
  created() {
    // Read once: the tokens don't change at runtime, and each read makes the browser recalculate styles.
    this.colors = cssTokens({
      '--color-primary': '#5667ff',
      '--color-progress': '#4fde7b',
      '--color-text': '#363647',
      '--color-text-inverse': '#ffffff',
      '--color-text-secondary': '#7f8fa4',
    });
  },
  beforeDestroy() {
    this.stopFollowingSize();
  },
  methods: {
    // No routes means every route.
    // An empty list means the user cleared the filter; the dashboard keeps it cleared.
    selectRoutes(routeIds) {
      this.$emit('riders-refresh', routeIds);
    },
    // The chart fills the tile (at least its 400px), which grows with Ride Requests beside it. Highcharts 9 only
    // resizes on window resizes, so a ResizeObserver tells it when the tile changes.
    followSize() {
      const { chartArea } = this.$refs;
      if (!chartArea || typeof ResizeObserver === 'undefined') return;
      this.stopFollowingSize();
      this.resizeObserver = new ResizeObserver(() => this.$refs.chart?.chart?.reflow());
      this.resizeObserver.observe(chartArea);
    },
    stopFollowingSize() {
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
    },
    handleExport(exportFormat) {
      const chart = this.$refs.chart?.chart;
      if (exportFormat === 'png') exportChartAsPNG(chart, 'number-of-riders');
      else if (exportFormat === 'pdf') exportChartAsPDF(chart, 'number-of-riders');
      else if (exportFormat === 'print') printChart(chart);
      else if (exportFormat === 'excel') this.downloadExcel();
    },
    // The ridership report, as Occupancy Ratios exports it, for the routes on screen.
    async downloadExcel() {
      if (this.exportRouteIds === null) {
        this.getToaster('warning', this.$t('flexigo.ridership.noRoutesInRouteGroups'));
        return;
      }
      this.isDownloading = true;
      try {
        const { startDay, endDay } = this.reportDays;
        await downloadRidershipReport({ startDay, endDay, routeIds: this.exportRouteIds });
      } catch (error) {
        console.error(error);
      } finally {
        this.isDownloading = false;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
// Fills what the title row leaves of the tile; Highcharts takes its height from here. A zero basis, so a chart
// drawn taller doesn't keep the row tall once Ride Requests shrinks again.
.chart-area {
  flex: 1 1 0;
  min-height: 400px;
}
</style>
